import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { Radio, RadioGroup } from "@mui/material";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { useFetch } from "services/hooks/useFetch";
import { themes } from "services/constants";
import { CustomTextBox } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { otTimingOptions } from "pages/TeamZone/TeamZoneHeaders";
import {
  optionsStyle,
  cardStyle,
} from "pages/HR/Shift/Configuration/ShiftCategory/Component/ShiftCategoryOptions";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalSidePanel,
  PanelHeaderIconButton,
} from "components/DesignSystem/GlobalSidePanel";
import RequestStatusTimeline from "components/DesignSystem/RequestStatusTimeline";
import ApprovalEditAuditTrail from "components/DesignSystem/ApprovalEditAuditTrail";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import {
  detailAccentPalette,
  detailCardSx,
  detailSectionLabelSx,
  RequestDetailField,
} from "components/DesignSystem/RequestDetailField";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import {
  designColors,
  designRadii,
  designShell,
} from "services/constants/designTokens";
import {
  GlobalFieldLabel,
  GlobalConfirmDialog,
} from "components/DesignSystem/sharedUiPatterns";
import { approvePanelUi, useGoToApprovalCenter } from "../approveUi";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { canonicalOverTimeStatus, overTimeStatusVisual } from "./OverTimeStatusCards";

const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

const detailAccent = {
  date: detailAccentPalette.indigo,
  duration: detailAccentPalette.green,
  timing: detailAccentPalette.blue,
  workflow: detailAccentPalette.purple,
  reason: detailAccentPalette.orange,
  rejectReason: detailAccentPalette.purple,
};

const WorkflowChip = ({ mode }) => {
  const isPlanned = mode === "Planned";
  const accent = isPlanned ? "#2563EB" : "#D97706";
  const tint = isPlanned ? "#EFF6FF" : "#FFFBEB";
  return (
    <Mui.Chip
      size="small"
      label={isPlanned ? "Planned OT" : "Completed OT"}
      sx={{
        height: 26,
        borderRadius: designRadii.pill,
        backgroundColor: tint,
        color: accent,
        border: `1px solid ${accent}`,
        "& .MuiChip-label": { px: 1, fontSize: 12.5, fontWeight: 600 },
      }}
    />
  );
};

export const OverTimeDetailPanel = ({
  open,
  onClose,
  request,
  checkAccess,
  setReCallApi,
  expanded = false,
  onToggleExpand,
}) => {
  const goToApprovalCenter = useGoToApprovalCenter();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  // No dedicated cancel-an-approved-overtime endpoint exists yet, same gap
  // as Comp Off (see CompOffDetailPanel) — this reuses the same
  // approve/reject endpoint below with status: "Cancelled" as a first
  // attempt, needs verifying against the real backend.
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  // See Leave/LeaveDetailPanel.js — Approve/Reject/Cancel, the
  // edit-then-approve submitEdit flow (always sends status:"Approved"),
  // and the Edit trigger button that leads into it all used to be gated by
  // plain editor permission (checkAccess). Now requires real approver
  // permission (either level counts).
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Overtime",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Overtime",
    );
  // The Edit trigger itself is the one action here that's still meant to
  // require editor access specifically — approve/reject/cancel above are
  // approver-only on purpose, but modifying the request's own fields (date,
  // duration, timing, reason) before it submits as Approved is an edit, so
  // an approve-only role (editor: false) shouldn't even see this button, not
  // just have it disabled. checkAccess is the plain editor permission
  // passed down from OverTime/index.js — unused everywhere else in this
  // file since every other action already moved to hasApproverAccess.
  const canEditRequest = checkAccess && hasApproverAccess;
  const { result: employees } = useFetch(
    `${domain}list/employee?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({ mode: "onBlur", defaultValues: { rejected_reason: "" } });

  useEffect(() => {
    register("rejected_reason", {
      required: "Reject reason is required",
      validate: {
        minLengthNoSpaces: (value) => {
          if (value?.trim().length < 3) return minimum3Characters;
          if (value?.trim().length > 255) return "Maximum length is 255 characters";
          return true;
        },
        atleastOneCharacterWithSpaceReg: (value) =>
          atleastOneCharacterWithSpace.test(value) || "Atleast one alphabet required",
      },
    });
  }, [register]);

  const {
    register: registerEdit,
    handleSubmit: handleSubmitEdit,
    setValue: setEditValue,
    watch: watchEdit,
    control: editControl,
    reset: resetEdit,
    formState: { errors: editErrors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      start_date: null,
      end_date: null,
      durationHrs: null,
      applicable_timing: "Only Before",
      reason: "",
    },
  });
  const [showEditAvatar, setShowEditAvatar] = useState(false);

  useEffect(() => {
    setRejectDialogOpen(false);
    setEditMode(false);
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
    reset({ rejected_reason: "" });
    resetEdit({
      user_name: request?.employee_name || "",
      user_id: request?.user_id,
      start_date: request?.ot_date ? dayjs(toISODate(request.ot_date)) : null,
      end_date: request?.ot_date ? dayjs(toISODate(request.ot_date)) : null,
      durationHrs: request?.ot_duration
        ? dayjs(request.ot_duration, "HH:mm")
        : null,
      applicable_timing: request?.ot_consideration || "Only Before",
      reason: request?.reason || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, reset, resetEdit]);

  const statusFlowData = useMemo(
    () => ({
      ...(request || {}),
      applied_by:
        request?.applied_by ||
        request?.employee_name ||
        request?.username ||
        request?.requested_by,
      applied_on:
        request?.applied_on ||
        request?.applied_date ||
        request?.created_at ||
        request?.created_date ||
        request?.ot_date,
      // request?.approver_name is the assigned approver, stamped at apply
      // time before anyone has acted -- it is not proof of approval and
      // must not be preferred over (or substitute for) the backend's
      // status-derived approved_by.
      approved_by: request?.approved_by,
      approved_at: request?.approved_at || request?.approved_on,
      rejected_by: request?.rejected_by,
      rejected_at: request?.rejected_at || request?.rejected_on,
    }),
    [request],
  );

  if (!request) return null;

  const status = canonicalOverTimeStatus(request?.status);
  const isPending = status === "Pending";
  // Requested per user: allow cancelling an overtime request even after it
  // has been Approved (not just while Pending), same as Comp Off.
  const canCancelApproved = status === "Approved";
  const employeeName = request?.employee_name || "N/A";
  const employeeId = request?.employee_id || request?.emp_id || "-";
  const department = request?.department_name || request?.department || "-";
  const designation = request?.designation_name || request?.designation || "-";
  const requestReason = request?.reason || request?.description || "-";
  // rejected_reason is the one shared reason column the backend also writes
  // a cancellation reason into -- the label must track actual status
  // instead of always reading "Reject Reason", matching the convention used
  // by OnDuty/CompOff/Permission's detail panels for the same model shape.
  const rejectedReason = request?.rejected_reason || "";
  const decisionReasonLabel =
    status === "Cancelled" ? "Cancel Reason" : "Reject Reason";

  const handleCancelEdit = () => {
    resetEdit({
      user_name: request?.employee_name || "",
      user_id: request?.user_id,
      start_date: request?.ot_date ? dayjs(toISODate(request.ot_date)) : null,
      end_date: request?.ot_date ? dayjs(toISODate(request.ot_date)) : null,
      durationHrs: request?.ot_duration
        ? dayjs(request.ot_duration, "HH:mm")
        : null,
      applicable_timing: request?.ot_consideration || "Only Before",
      reason: request?.reason || "",
    });
    setEditMode(false);
  };

  const handleCloseAction = () => {
    if (editMode) {
      handleCancelEdit();
      return;
    }
    onClose?.();
  };

  const updateEditUserName = (event, data) => {
    if (data) {
      setEditValue("user_name", data);
      setEditValue("user_id", data?.user_id);
      setShowEditAvatar(false);
    } else {
      setEditValue("user_name", null);
      setEditValue("user_id", "");
      setShowEditAvatar(true);
    }
  };

  const handleEditApplicableTiming = (event) => {
    setEditValue("applicable_timing", event.target.value);
  };

  const submitEdit = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this overtime request",
      );
      return;
    }
    setSaving(true);
    const payload = {
      pre_ot_ids: [request.id],
      ot_start_date: data.start_date?.format("YYYY-MM-DD"),
      ot_end_date: data.end_date?.format("YYYY-MM-DD"),
      ot_duration: dayjs(data.durationHrs).format("HH:mm"),
      ot_consideration: data.applicable_timing,
      reason: data.reason,
      status: "Approved",
      payout_policy: "Backlog",
    };
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/teamworkspace-ot-approve/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast(
        "success",
        response?.responseDetails?.message || "Overtime request approved.",
      );
      setReCallApi?.(true);
      setEditMode(false);
      onClose?.();
    } else {
      showToast(
        "error",
        response?.message ||
          "Couldn't approve the overtime request. Please try again.",
      );
    }
    setSaving(false);
  };

  const updatePlannedStatus = async (requestStatus, reason) => {
    const payload = {
      pre_ot_ids: [request.id],
      ot_date: toISODate(request?.ot_date),
      ot_duration: request?.ot_duration,
      ot_consideration: request?.ot_consideration,
      status: requestStatus,
      payout_policy: "Backlog",
      ...(requestStatus === "Rejected"
        ? { rejected_reason: reason }
        : requestStatus === "Cancelled"
          ? { cancelled_reason: reason }
          : { reason: request?.reason }),
    };
    const response = await customAPI(
      `${domain}Team_Zone/Approve/Over_time/teamworkspace-ot-approve/?company_id=${topbarCompanyId}`,
      "PUT",
      payload,
    );
    if (response?.ok) {
      showToast(
        requestStatus === "Rejected" ? "error" : "success",
        response?.responseDetails?.message ||
          `Overtime request ${requestStatus.toLowerCase()}.`,
      );
      setReCallApi?.(true);
      setRejectDialogOpen(false);
      setCancelDialogOpen(false);
      onClose?.();
      return true;
    }
    showToast(
      "error",
      response?.message ||
        `Couldn't ${
          requestStatus === "Approved"
            ? "approve"
            : requestStatus === "Cancelled"
              ? "cancel"
              : "reject"
        } the overtime request. Please try again.`,
    );
    return false;
  };

  const handleApprove = async () => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to approve this overtime request",
      );
      return;
    }
    setApproving(true);
    await updatePlannedStatus("Approved");
    setApproving(false);
  };

  const submitReject = async (data) => {
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to reject this overtime request",
      );
      return;
    }
    setRejecting(true);
    await updatePlannedStatus("Rejected", data?.rejected_reason);
    setRejecting(false);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    reset({ rejected_reason: "" });
    clearErrors("rejected_reason");
  };

  const submitCancel = async () => {
    const trimmed = cancelReason.trim();
    if (!trimmed) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmed.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }
    if (!hasApproverAccess) {
      showToast(
        "error",
        "Access denied: You are not authorized to cancel this overtime request",
      );
      return;
    }
    setCancelling(true);
    await updatePlannedStatus("Cancelled", trimmed);
    setCancelling(false);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const editContent = (
    <Mui.Box component="form" onSubmit={handleSubmitEdit(submitEdit)}>
      <Mui.Grid container spacing={2.5}>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Select the employee requesting or assigned to work overtime.">
            Employee Name
          </GlobalFieldLabel>
          <CustomAutoCompleteWithLogo
            dataList={employees?.map((obj) => ({
              ...obj,
              label: obj.user_name,
              image: obj?.image_url,
            }))}
            selectedValue={watchEdit("user_name")}
            rules={{
              ...registerEdit("user_name", {
                required: "Employee name is required",
              }),
            }}
            updateDataFunction={updateEditUserName}
            placeHolder="Select employee name"
            errorMessage={editErrors?.user_name?.message || ""}
            textBoxError={Boolean(editErrors.user_name)}
            showAvathar={showEditAvatar}
            setShowAutoCompleteAvathar={setShowEditAvatar}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Update the start date for the overtime period">
            Start Date
          </GlobalFieldLabel>
          <CustomizedDatePicker
            name="start_date"
            control={editControl}
            required
            requiredMessage="Start date is required"
            placeholder="Select start date"
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Update the end date for the overtime period">
            End Date
          </GlobalFieldLabel>
          <CustomizedDatePicker
            name="end_date"
            control={editControl}
            required
            requiredMessage="End date is required"
            placeholder="Select end date"
            startDate={watchEdit("start_date")}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Enter the maximum allowed overtime hours as defined by the OT Policy.">
            Duration in Hrs
          </GlobalFieldLabel>
          <CustomTimePicker
            name="durationHrs"
            control={editControl}
            errors={editErrors}
            validateFn={(value) => {
              if (!value || !dayjs(value).isValid()) {
                return "Duration hours is required";
              }
              return true;
            }}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Select when the overtime will be considered">
            Applicable Timing
          </GlobalFieldLabel>
          <RadioGroup
            row
            value={watchEdit("applicable_timing")}
            onChange={handleEditApplicableTiming}
            sx={optionsStyle}
          >
            {otTimingOptions.map((opt) => {
              const isActive = watchEdit("applicable_timing") === opt.id;
              return (
                <Mui.Box key={opt.id} sx={cardStyle(isActive)}>
                  <Mui.Box sx={{ display: "flex", alignItems: "center" }}>
                    <Radio
                      value={opt.id}
                      sx={{
                        "&.Mui-checked": { color: themes.stepActiveColor },
                      }}
                    />
                    <Mui.Typography
                      fontWeight="medium"
                      sx={{
                        color: isActive ? themes.stepActiveColor : "inherit",
                      }}
                    >
                      {opt.label}
                    </Mui.Typography>
                  </Mui.Box>
                  <Mui.Typography
                    variant="body2"
                    color={designColors.slate600}
                    sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                  >
                    {opt.description}
                  </Mui.Typography>
                </Mui.Box>
              );
            })}
          </RadioGroup>
        </Mui.Grid>
        <Mui.Grid item xs={12}>
          <GlobalFieldLabel helperText="Mention the purpose or justification for the overtime request">
            Reason
          </GlobalFieldLabel>
          <CustomTextBox
            type="multiline"
            rules={{
              ...registerEdit("reason", {
                required: "Reason is required",
                validate: {
                  minLengthNoSpaces: (value) => {
                    if (value?.trim().length < 3 && value !== "") {
                      return minimum3Characters;
                    }
                    if (value?.trim().length > 255 && value !== "") {
                      return "Maximum length is 255 characters";
                    }
                    return true;
                  },
                  atleastOneCharacterWithSpaceReg: (value) =>
                    atleastOneCharacterWithSpace.test(value) ||
                    value === "" ||
                    "Atleast one alphabet required",
                },
              }),
            }}
            selectedValue={watchEdit("reason")}
            updateDataFunction={(event) => {
              if (event.target.value.length <= 1024) {
                setEditValue("reason", event.target.value);
              }
            }}
            placeHolder="Provide a reason for overtime"
            errorMessage={editErrors?.reason?.message || ""}
            textBoxError={Boolean(editErrors.reason)}
          />
        </Mui.Grid>
      </Mui.Grid>
    </Mui.Box>
  );

  // editMode's own footer is only ever reached via the Edit trigger above,
  // which now requires canEditRequest (checkAccess && hasApproverAccess) to
  // even render — so hasApproverAccess is already guaranteed true anywhere
  // this branch shows, and the Approve button here no longer needs its own
  // gate.
  const footerContent = editMode ? (
    <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
      <SecondaryButton onClick={handleCancelEdit}>Cancel</SecondaryButton>
      <PrimaryButton
        onClick={handleSubmitEdit(submitEdit)}
        loading={saving}
        disabled={saving}
      >
        Approve
      </PrimaryButton>
    </Mui.Stack>
  ) : isPending ? (
    // GlobalSidePanel only draws the footer's border/background at all when
    // its `footer` prop is truthy — an empty-but-rendered <Mui.Stack> here
    // used to still count as truthy, so a no-approver-access viewer got a
    // bare bordered strip with nothing in it. Falling through to a real
    // `null` for "nothing to show" (here and in the canCancelApproved
    // branch below) lets that check actually skip the footer.
    hasApproverAccess ? (
      <Mui.Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        <Mui.Button
          variant="outlined"
          startIcon={<HighlightOffIcon />}
          onClick={() => setRejectDialogOpen(true)}
          sx={{
            ...designShell.panelDangerButton,
            color: "#B42318",
            borderColor: "#F5C2C7",
            backgroundColor: "#FEF3F2",
            "&:hover": {
              borderColor: "#E59CA5",
              backgroundColor: "#FEE4E2",
            },
          }}
        >
          Reject
        </Mui.Button>
        <PrimaryButton
          startIcon={<CheckCircleOutlineIcon />}
          loading={approving}
          onClick={handleApprove}
        >
          Approve
        </PrimaryButton>
      </Mui.Box>
    ) : null
  ) : canCancelApproved ? (
    hasApproverAccess ? (
      <Mui.Stack spacing={1.5}>
        <Mui.Typography
          sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
        >
          This request has already been approved.
        </Mui.Typography>
        <RequestCancelButton onClick={() => setCancelDialogOpen(true)}>
          Cancel Overtime
        </RequestCancelButton>
      </Mui.Stack>
    ) : null
  ) : (
    <Mui.Typography
      sx={{ ...approvePanelUi.panelMutedText, textAlign: "center" }}
    >
      This request has already been {status?.toLowerCase() || "processed"}.
    </Mui.Typography>
  );

  const rejectDialog = (
    <GlobalConfirmDialog
      open={rejectDialogOpen}
      title="Reject Overtime Request"
      onCancel={closeRejectDialog}
      onConfirm={() => {
        if (!watch("rejected_reason")?.trim()) {
          setError("rejected_reason", {
            type: "required",
            message: "Reject reason is required",
          });
          return;
        }
        handleSubmit(submitReject)();
      }}
      confirmLabel="Reject"
      cancelLabel="Cancel"
      loading={rejecting}
      danger
      reasonValue={watch("rejected_reason") || ""}
      onReasonChange={(value) =>
        setValue("rejected_reason", value, { shouldValidate: true })
      }
      reasonError={errors?.rejected_reason?.message || ""}
      reasonPlaceholder="Provide a reason for rejection"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        Enter the reason for rejecting this overtime request.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  const cancelDialog = (
    <GlobalConfirmDialog
      open={cancelDialogOpen}
      title="Cancel Overtime Request"
      onCancel={closeCancelDialog}
      onConfirm={submitCancel}
      confirmLabel="Continue"
      cancelLabel="Keep"
      loading={cancelling}
      loadingLabel="Cancelling..."
      danger
      reasonValue={cancelReason}
      onReasonChange={(value) => {
        setCancelReason(value);
        if (cancelReasonError) setCancelReasonError("");
      }}
      reasonError={cancelReasonError}
      reasonPlaceholder="Provide a reason for cancelling this approved overtime request"
      reasonMinRows={2}
      maxWidth="sm"
    >
      <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
        This overtime request has already been approved. Enter a reason to
        cancel it.
      </Mui.Typography>
    </GlobalConfirmDialog>
  );

  return (
    <>
      <GlobalSidePanel
        open={open}
        onClose={handleCloseAction}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        title="Overtime Request"
        subtitle="Request details and status"
        headerActions={
          isPending && !editMode && canEditRequest ? (
            // Icon in both modes, bigger in the full view (2026-10-01).
            <PanelHeaderIconButton
              expanded={expanded}
              title="Edit overtime request"
              onClick={() => setEditMode(true)}
            >
              <EditIcon />
            </PanelHeaderIconButton>
          ) : null
        }
        headerSx={{ px: 2.5, pt: 2.5, pb: 1.5 }}
        PaperProps={{
          sx: {
            backgroundColor: designColors.white,
            maxWidth: "92vw",
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            overflow: "hidden",
          },
        }}
        breadcrumb={[
          { label: "Approval Center", onClick: goToApprovalCenter },
          { label: "Overtime", onClick: handleCloseAction },
          employeeName || "Overtime Request",
        ]}
        footer={footerContent}
      >
        <Mui.Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          {editMode ? (
            <>
              <RequestDetailHeader
                userDetails={{
                  image_url: request?.image_url,
                  employee_id: employeeId,
                  user_name: employeeName,
                  department_name: department,
                  designation,
                }}
                status={status}
                colorCode={request?.color_code || overTimeStatusVisual(status).accent}
              />
              <Mui.Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 0.5,
                }}
              >
                <Mui.Typography sx={{ ...approvePanelUi.panelMutedText }}>
                  Editing this overtime request's details.
                </Mui.Typography>
              </Mui.Box>
              {editContent}
            </>
          ) : (
            <>
              <RequestDetailHeader
                userDetails={{
                  image_url: request?.image_url,
                  employee_id: employeeId,
                  user_name: employeeName,
                  department_name: department,
                  designation,
                }}
                status={status}
                colorCode={request?.color_code || overTimeStatusVisual(status).accent}
              />

              <Mui.Box sx={detailCardSx}>
                <Mui.Typography sx={{ ...detailSectionLabelSx, mb: 1.75 }}>
                  Request Details
                </Mui.Typography>
                <RequestDetailField
                  icon={CalendarMonthIcon}
                  label="OT Date"
                  value={request?.ot_date || "-"}
                  accent={detailAccent.date}
                />
                <RequestDetailField
                  icon={TimerOutlinedIcon}
                  label="Duration"
                  value={request?.ot_duration || "-"}
                  accent={detailAccent.duration}
                />
                <RequestDetailField
                  icon={AccessTimeIcon}
                  label="Applicable Timing"
                  value={request?.ot_consideration || "-"}
                  accent={detailAccent.timing}
                />
                <RequestDetailField
                  icon={CategoryOutlinedIcon}
                  label="Workflow"
                  value={request?.approval_mode || "-"}
                  accent={detailAccent.workflow}
                  rightPill={<WorkflowChip mode={request?.approval_mode} />}
                />
                <RequestDetailField
                  icon={DescriptionOutlinedIcon}
                  label="Reason"
                  value={requestReason}
                  accent={detailAccent.reason}
                  multiline
                  isLast={!rejectedReason}
                />
                {rejectedReason ? (
                  <RequestDetailField
                    icon={DescriptionOutlinedIcon}
                    label={decisionReasonLabel}
                    value={rejectedReason}
                    accent={detailAccent.rejectReason}
                    multiline
                    isLast
                  />
                ) : null}
              </Mui.Box>

              <RequestStatusTimeline
            endDate={request?.ot_date}
                data={statusFlowData}
                variant="stepper"
                hidePendingStep
              />
              <ApprovalEditAuditTrail data={request} />
            </>
          )}
        </Mui.Box>
      </GlobalSidePanel>
      {rejectDialog}
      {cancelDialog}
    </>
  );
};
